import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Grid,
  Divider,
  Button,
  CircularProgress,
  Avatar,
  Alert,
  useTheme,
  useMediaQuery,
  Fade
} from '@mui/material';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Check as CheckIcon
} from '@mui/icons-material';

export default function CustomerForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: '',
    contactNumber: '',
    category: '',
    companyName: '',
    companyURL: '',
    address: '',
    country: '233',
    state: '',
    city: '',
    zipcode: ''
  });
  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (formData.state) {
      fetchCitiesByState(formData.state);
    } else {
      setCities([]);
      setFormData(prev => ({ ...prev, city: '' }));
    }
  }, [formData.state]); const fetchCustomers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/customer-contracts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customers: ${response.status}`);
      }

      const data = await response.json();
      if (!data) {
        throw new Error('No data received from server');
      }

      // Adjust based on your API response structure
      const customerList = Array.isArray(data) ? data : data.customers || [];
      setCustomers(customerList);
      setSubmitStatus({ type: 'success', message: 'Customers loaded successfully' });
    } catch (error) {
      console.error('Error fetching customers:', error);
      setCustomers([]);
      setSubmitStatus({
        type: 'error',
        message: `Unable to load customers: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    setStatesLoading(true);
    try {
      const response = await fetch('https://crmapi.jobsnprofiles.com/common/get_states_by_countryid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ country_id: formData.country })
      });
      if (!response.ok) throw new Error('Failed to fetch states');
      const data = await response.json();
      const statesList = data.data || data.states || data || [];
      const formatted = Array.isArray(statesList)
        ? statesList.map(s => ({ id: s.state_id || s.id, name: s.state_name || s.name }))
        : [];
      setStates(formatted);
    } catch (err) {
      setSubmitStatus({ type: 'error', message: 'Failed to fetch states. Please try again.' });
    } finally {
      setStatesLoading(false);
    }
  };

  const fetchCitiesByState = async (stateId) => {
    setCitiesLoading(true);
    try {
      const response = await fetch('https://crmapi.jobsnprofiles.com/common/get_cities_by_stateid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ state_id: stateId })
      });
      if (!response.ok) throw new Error('Failed to fetch cities');
      const data = await response.json();
      const cityList = data.data || [];
      const formatted = Array.isArray(cityList)
        ? cityList.map(c => ({ id: c.id, name: c.cityName }))
        : [];
      setCities(formatted);
    } catch (err) {
      setSubmitStatus({ type: 'error', message: 'Failed to fetch cities. Please try again.' });
    } finally {
      setCitiesLoading(false);
    }
  };
  const validateForm = () => {
    const newErrors = {};

    // Personal Information validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (!/^[A-Za-z\s]{2,50}$/.test(formData.fullName.trim())) {
      newErrors.fullName = 'Name should contain only letters and spaces (2-50 characters)';
    }    const emailError = validateEmail(formData.email);
    if (emailError) {
      newErrors.email = emailError;
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = 'Contact number is required';
    } else {
      // Remove all non-digit characters for validation
      const cleanNumber = formData.contactNumber.replace(/\D/g, '');
      if (!/^\d{10,15}$/.test(cleanNumber)) {
        newErrors.contactNumber = 'Phone number must be 10-15 digits';
      }
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    // Company Information validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    } else if (!/^[A-Za-z0-9\s&.,'-]{2,100}$/.test(formData.companyName.trim())) {
      newErrors.companyName = 'Invalid company name format. Use letters, numbers, and common symbols only';
    }

    // if (!formData.category) {
    //   newErrors.category = 'Category is required';
    // }

    if (!formData.companyURL) {
      newErrors.companyURL = 'Company URL is required';
    } else if (!/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/.test(formData.companyURL)) {
      newErrors.companyURL = 'Please enter a valid URL (e.g., https://company.com)';
    }

    // Address Information validation
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.city) newErrors.city = 'City is required';    if (formData.zipcode.trim() && !/^\d{5}(-\d{4})?$/.test(formData.zipcode)) {
      newErrors.zipcode = 'Invalid ZIP code format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };  const validateEmail = (email) => {
    if (!email.trim()) {
      return 'Email is required';
    } 
    if (!/^[A-Z0-9._%+-]+@(?:[A-Z0-9-]+\.)+[A-Z]{2,}$/i.test(email)) {
      return 'Invalid email format';
    }
    
    const freeEmailDomains = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'aol.com', 'icloud.com', 'mail.com', 'zoho.com',
      'proton.me', 'protonmail.com', 'yandex.com', 'live.com',
      'msn.com', 'me.com', 'inbox.com', 'rediffmail.com'
    ];
    const emailDomain = email.split('@')[1]?.toLowerCase();
    if (emailDomain && freeEmailDomains.includes(emailDomain)) {
      return 'Please use your company email address. Personal email addresses are not accepted.';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let formattedValue = value;

    // Real-time email validation
    if (name === 'email') {
      const emailError = validateEmail(value);
      setErrors(prev => ({ ...prev, [name]: emailError }));
    }

    // Special handling for contact number formatting
    if (name === 'contactNumber') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');

      // Format the phone number as user types
      if (digitsOnly.length <= 10) {
        // Format as: (XXX) XXX-XXXX
        formattedValue = digitsOnly.replace(/(\d{1,3})(\d{1,3})?(\d{1,4})?/, (_, p1, p2, p3) => {
          let number = `${p1 || ''}`;
          if (p2) number += `-${p2}`;
          if (p3) number += `-${p3}`;
          return number;
        });
      } else {
        // For international numbers: +X-XXX-XXX-XXXX
        formattedValue = digitsOnly.replace(/(\d{1,3})(\d{1,3})?(\d{1,3})?(\d{1,4})?/, (_, p1, p2, p3, p4) => {
          let number = `+${p1 || ''}`;
          if (p2) number += `-${p2}`;
          if (p3) number += `-${p3}`;
          if (p4) number += `-${p4}`;
          return number;
        });
      }
    }

    setFormData(prev => ({ ...prev, [name]: formattedValue }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Prepare payload according to the new API format
      const payload = {
        email: formData.email,
        comp_url: formData.companyURL || '',
        full_name: formData.fullName,
        mobile: formData.contactNumber,
        countryid: parseInt(formData.country),
        stateid: parseInt(formData.state),
        cityid: parseInt(formData.city),
        role: formData.role,
        comp_name: formData.companyName,
        category: 1,
        description: "",
        address: formData.address
      };

      console.log('Submitting form data:', payload);

      const response = await fetch('https://crmapi.jobsnprofiles.com/customer/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);

      let data;
      const contentType = response.headers.get('content-type');      let responseData;
      try {
        // Try to parse as JSON first
        responseData = await response.json();
      } catch (e) {
        // If JSON parsing fails, get as text
        const text = await response.text();
        responseData = { message: text };
      }
      console.log('API Response:', responseData);

      if (!response.ok) {
        // Get the error message from the API response
        const errorMessage = responseData.error || responseData.message || `Failed to create customer: ${response.status}`;
        throw new Error(errorMessage);
      }

      // Store user_id and company_id in local emp_subscriptions table if present in response
      if (responseData && responseData.data && responseData.data.user_id && responseData.data.company_id) {
        try {
          const localPayload = {
            user_id: responseData.data.user_id,
            company_id: responseData.data.company_id,
           
           
          };
          await fetch(`${process.env.REACT_APP_API_URL}/emp-subscriptions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(localPayload)
          });
        } catch (localError) {
          console.error('Error saving to local emp_subscriptions:', localError);
        }
      }

      setSubmitStatus({
        type: 'success',
        message: 'Customer registration completed successfully!'
      });

  handleReset();
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    setFormData({
      fullName: '',
      email: '',
      role: '',
      contactNumber: '',
      category: '',
      companyName: '',
      companyURL: '',
      address: '',
      country: '233',
      state: '',
      city: '',
      zipcode: ''
    });
    setErrors({});
  };
  const categories = ['Enterprise', 'Small Business', 'Startup', 'Individual', 'Non-Profit', 'Government', 'Other'];
  const roles = ['Employer', 'Employee', 'Agent'];
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* ...existing code... */}
      </Box>

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Personal Information
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                error={!!errors.fullName}
                helperText={errors.fullName}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "25%" }}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                fullWidth
                type="email"
                variant="outlined"
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>            <Grid item xs={12} md={6} sx={{ width: "25%" }}>              <PhoneInput
                country={'us'}
                value={formData.contactNumber}
                onChange={(phone) => setFormData(prev => ({ ...prev, contactNumber: phone }))}
                inputStyle={{
                  width: '100%',
                  height: '56px',
                  fontSize: '16px',
                  borderColor: errors.contactNumber ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)',
                  borderRadius: '4px'
                }}
                preferredCountries={['us']}
                enableSearch={true}
                disableSearchIcon={true}
                searchPlaceholder="Search country..."
                containerStyle={{
                  width: '100%'
                }}
                inputProps={{
                  required: true,
                  name: 'contactNumber'
                }}
                buttonStyle={{
                  borderRadius: '4px 0 0 4px',
                  borderColor: errors.contactNumber ? '#d32f2f' : 'rgba(0, 0, 0, 0.23)'
                }}
              />
              {errors.contactNumber && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 2 }}>
                  {errors.contactNumber}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "20%" }}>
              <TextField
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                error={!!errors.role}
                helperText={errors.role}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Company Information
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                error={!!errors.companyName}
                helperText={errors.companyName}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ width: "25%" }}>
              <TextField
                label="Company URL"
                name="companyURL"
                value={formData.companyURL}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                error={!!errors.companyURL}
                helperText={errors.companyURL}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2 }}>
            Address Information
          </Typography>
          <Grid container spacing={3} >
            <Grid item xs={12} md={4} sx={{ width: "20%" }}>
              <TextField
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                error={!!errors.address}
                helperText={errors.address}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ width: "25%" }}>
              <TextField
                select
                label="State"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                disabled={statesLoading}
                error={!!errors.state}
                helperText={errors.state}
              >
                {statesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                  </MenuItem>
                ) : (
                  states.map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4} sx={{ width: "25%" }}>
              <TextField
                select
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                fullWidth
                variant="outlined"
                disabled={!formData.state || citiesLoading}
                error={!!errors.city}
                helperText={errors.city}
              >
                {citiesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={24} />
                  </MenuItem>
                ) : (
                  cities.map(c => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4} sx={{ width: "20%" }}>              <TextField
                label="ZIP Code"
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                error={!!errors.zipcode}
                helperText={errors.zipcode}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleReset}
              size="large"
            >
              Reset
            </Button>
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckIcon />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}